import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

function DashboardPage() {
  const [messages, setMessages] = useState([]);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({ old_password: '', new_password: '' });
  const [passwordMsg, setPasswordMsg] = useState('');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addUserData, setAddUserData] = useState({ username: '', password: '' });
  const [addUserMsg, setAddUserMsg] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    const API_URL = process.env.REACT_APP_API_URL || '';

    fetch(`${API_URL}/api/contact`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(res => {
        if (res.status === 401) {
            localStorage.removeItem('token');
            navigate('/login');
            throw new Error('Unauthorized');
        }
        return res.json();
    })
    .then(data => {
      if (Array.isArray(data)) {
        setMessages(data);
      } else {
        console.error("API did not return an array:", data);
        setMessages([]);
      }
    })
    .catch(err => console.error(err));
  }, [navigate]);

  const handleLogout = () => {
      localStorage.removeItem('token');
      navigate('/login');
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;

    const token = localStorage.getItem('token');
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/contact/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setMessages(messages.filter(msg => msg.id !== id));
      } else {
        alert("Failed to delete message.");
      }
    } catch (error) {
      console.error("Error deleting message:", error);
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMsg('');
    const token = localStorage.getItem('token');

    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(passwordData)
      });
      const data = await response.json();
      setPasswordMsg(data.message);
      if (response.ok) {
        setTimeout(() => { setShowPasswordModal(false); setPasswordMsg(''); setPasswordData({ old_password: '', new_password: '' }); }, 2000);
      }
    } catch (error) {
      setPasswordMsg("Error updating password");
    }
  };

  const handleAddUserChange = (e) => {
    setAddUserData({ ...addUserData, [e.target.name]: e.target.value });
  };

  const submitAddUser = async (e) => {
    e.preventDefault();
    setAddUserMsg('');
    const token = localStorage.getItem('token');

    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(addUserData)
      });
      const data = await response.json();
      setAddUserMsg(data.message);
      if (response.ok) {
        setTimeout(() => { setShowAddUserModal(false); setAddUserMsg(''); setAddUserData({ username: '', password: '' }); }, 2000);
      }
    } catch (error) {
      setAddUserMsg("Error creating user");
    }
  };

  return (
    <div className="bg-gray-50 text-deepgreen min-h-screen flex flex-col font-sans">
      <Navbar />
      <div className="flex-grow container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-2xl border-t-4 border-gold overflow-hidden">
          <div className="px-6 py-8 border-b border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
            <div>
              <h1 className="text-3xl font-serif font-bold text-gold">Admin Dashboard</h1>
              <p className="text-gray-500 mt-1 text-sm">Manage your client messages and inquiries</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowAddUserModal(true)} className="px-5 py-2 bg-gold text-deepgreen border border-gold rounded-lg hover:bg-white hover:text-deepgreen transition duration-300 text-sm font-bold shadow-sm">
                + Add Admin
              </button>
              <button onClick={() => setShowPasswordModal(true)} className="px-5 py-2 bg-white text-deepgreen border border-deepgreen rounded-lg hover:bg-deepgreen hover:text-white transition duration-300 text-sm font-semibold shadow-sm">
                Change Password
              </button>
              <button onClick={handleLogout} className="px-5 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-600 hover:text-white transition duration-300 text-sm font-semibold shadow-sm">
                Sign Out
              </button>
            </div>
          </div>
        
          <div className="p-6">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <span className="bg-deepgreen text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">{messages.length}</span>
              Client Messages
            </h2>

            {messages.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                <p className="text-gray-500 text-lg">No new messages found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Message</th>
                      <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {messages.map(msg => (
                      <tr key={msg.id} className="hover:bg-gray-50 transition duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{msg.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{msg.email}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{msg.message}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button 
                            onClick={() => handleDelete(msg.id)}
                            className="text-red-600 hover:text-red-900 font-semibold hover:underline"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Change Admin Password</h3>
            <form onSubmit={submitPasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                <input type="password" name="old_password" value={passwordData.old_password} onChange={handlePasswordChange} className="w-full border border-gray-300 p-2 rounded focus:ring-gold focus:border-gold" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input type="password" name="new_password" value={passwordData.new_password} onChange={handlePasswordChange} className="w-full border border-gray-300 p-2 rounded focus:ring-gold focus:border-gold" required />
              </div>
              {passwordMsg && <p className={`text-sm ${passwordMsg.includes('success') ? 'text-green-600' : 'text-red-600'}`}>{passwordMsg}</p>}
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowPasswordModal(false)} className="px-4 py-2 text-gray-600 hover:text-gray-800">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-gold text-deepgreen font-bold rounded hover:bg-opacity-90">
                  Update Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Create New Admin</h3>
            <form onSubmit={submitAddUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <input type="text" name="username" value={addUserData.username} onChange={handleAddUserChange} className="w-full border border-gray-300 p-2 rounded focus:ring-gold focus:border-gold" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input type="password" name="password" value={addUserData.password} onChange={handleAddUserChange} className="w-full border border-gray-300 p-2 rounded focus:ring-gold focus:border-gold" required />
              </div>
              {addUserMsg && <p className={`text-sm ${addUserMsg.includes('success') ? 'text-green-600' : 'text-red-600'}`}>{addUserMsg}</p>}
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowAddUserModal(false)} className="px-4 py-2 text-gray-600 hover:text-gray-800">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-gold text-deepgreen font-bold rounded hover:bg-opacity-90">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
export default DashboardPage;